/* eslint-disable react/prop-types */
import { useState, useRef, useCallback } from "react";
import { FaPlayCircle } from 'react-icons/fa';
import { Gallery, Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import { normalizeUrl } from '../PropertyPhotoGallery';

/**
 * Builds and injects a thumbnail strip into the PhotoSwipe lightbox.
 * Same approach as PropertyPhotoGallery for visual consistency.
 * Supports both image and video items — video thumbs get a play icon overlay.
 */
const buildThumbnailStrip = (pswp, items) => {
  const strip = document.createElement('div');
  strip.className = 'pswp-thumbnail-strip';

  const inner = document.createElement('div');
  inner.className = 'pswp-thumbnail-strip__inner';
  strip.appendChild(inner);

  items.forEach((item, idx) => {
    const thumb = document.createElement('button');
    thumb.className = 'pswp-thumbnail-strip__thumb';
    if (item.type === 'video') {
      thumb.classList.add('pswp-thumbnail-strip__thumb--video');
    }
    thumb.setAttribute('aria-label', item.type === 'video' ? `Video ${idx + 1}` : (item.alt || `Photo ${idx + 1}`));
    thumb.setAttribute('type', 'button');

    const img = document.createElement('img');
    img.src = normalizeUrl(item.thumbnail || item.url);
    img.alt = item.type === 'video' ? 'Video thumbnail' : (item.alt || `Photo ${idx + 1}`);
    img.draggable = false;

    thumb.appendChild(img);

    // Add play icon overlay for video thumbnails
    if (item.type === 'video') {
      const playOverlay = document.createElement('span');
      playOverlay.className = 'pswp-thumbnail-strip__play-icon';
      playOverlay.innerHTML = '&#9654;'; // play triangle
      thumb.appendChild(playOverlay);
    }

    thumb.addEventListener('click', () => pswp.goTo(idx));
    inner.appendChild(thumb);
  });

  const syncActive = () => {
    const thumbs = inner.querySelectorAll('.pswp-thumbnail-strip__thumb');
    thumbs.forEach((t, i) => {
      t.classList.toggle('pswp-thumbnail-strip__thumb--active', i === pswp.currIndex);
    });
    const activeThumb = thumbs[pswp.currIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  pswp.on('change', syncActive);
  pswp.on('afterInit', syncActive);

  pswp.on('uiRegister', () => {
    pswp.ui.registerElement({
      name: 'thumbnail-strip',
      order: 9,
      isButton: false,
      appendTo: 'root',
      html: '',
      onInit: (el) => el.appendChild(strip),
    });

    // Image title in header
    pswp.ui.registerElement({
      name: 'category-label',
      order: 5,
      isButton: false,
      appendTo: 'bar',
      html: '<span class="pswp__category-label"></span>',
      onInit: (el) => {
        const labelEl = el.querySelector('.pswp__category-label');
        const updateTitle = () => {
          const item = items[pswp.currIndex];
          const title = item.type === 'video' ? 'Video Tour' : (item?.imageTitle || '');
          labelEl.textContent = title;
          labelEl.style.display = title ? '' : 'none';
        };
        pswp.on('change', updateTitle);
        pswp.on('afterInit', updateTitle);
      },
    });
  });
};

const HeroThumbnailStrip = ({
  images,
  videoData = [],
  selectedIndex,
  onThumbnailClick,
  onSeeAllClick,
  totalImageCount,
}) => {
  const [errorIndexes, setErrorIndexes] = useState({});
  const openFunctionsRef = useRef([]);

  const hasVideo = videoData && videoData.length > 0;

  // Build all gallery items: all images PLUS videos at the end
  const galleryItems = [
    ...(images || []).map((img) => ({
      type: 'image',
      url: normalizeUrl(img.url),
      thumbnail: normalizeUrl(img.thumbnail || img.url),
      alt: img.alt || '',
      category: img.category || '',
      imageTitle: img.imageTitle || '',
    })),
    ...videoData.map((videoUrl, idx) => ({
      type: 'video',
      url: videoUrl,
      thumbnail: images?.[2]?.thumbnail || images?.[2]?.url || images?.[0]?.thumbnail || images?.[0]?.url || '',
      alt: `Video tour ${idx + 1}`,
      imageTitle: 'Video Tour',
    })),
  ];

  const handleBeforeOpen = useCallback((pswp) => {
    buildThumbnailStrip(pswp, galleryItems);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, videoData]);

  if (!images || images.length === 0) {
    return null;
  }

  // Index where videos start in the gallery
  const videoStartIndex = images.length;

  // Build visible thumbnails: first 2 images, then video (if available) in 3rd spot, else 3rd image
  const visibleThumbnails = [];
  visibleThumbnails.push({ type: 'image', data: images[0], originalIndex: 0 });
  if (images.length > 1) {
    visibleThumbnails.push({ type: 'image', data: images[1], originalIndex: 1 });
  }
  if (hasVideo) {
    visibleThumbnails.push({ type: 'video', data: videoData[0], originalIndex: videoStartIndex });
  } else if (images.length > 2) {
    visibleThumbnails.push({ type: 'image', data: images[2], originalIndex: 2 });
  }

  const handleImageError = (index) => {
    setErrorIndexes((prev) => ({ ...prev, [index]: true }));
  };

  const handleSeeAllClick = () => {
    // Open PhotoSwipe at the first image
    if (openFunctionsRef.current[0]) {
      openFunctionsRef.current[0]();
    }
    if (onSeeAllClick) onSeeAllClick();
  };

  // Get video thumbnail for the 3rd visible position
  const videoThumb = images[2]?.thumbnail || images[2]?.url || images[0]?.thumbnail || images[0]?.url;

  return (
    <div className="commercial-hero__thumbnails">
      <Gallery onBeforeOpen={handleBeforeOpen} options={{ zoom: false }}>
        {/* Visible image thumbnails in hero */}
        {visibleThumbnails.filter(t => t.type === 'image').map((thumb, idx) => {
          const image = thumb.data;
          const imgIndex = thumb.originalIndex;
          return (
            <Item
              key={idx}
              original={normalizeUrl(image.url)}
              thumbnail={normalizeUrl(image.thumbnail || image.url)}
              width={1200}
              height={800}
            >
              {({ ref, open }) => {
                openFunctionsRef.current[imgIndex] = open;
                return (
                  <div
                    ref={ref}
                    className="commercial-hero__thumbnails-item"
                    onClick={open}
                    role="button"
                    tabIndex={0}
                    aria-label={image.alt || `Thumbnail ${idx + 1}`}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
                  >
                    {errorIndexes[imgIndex] ? (
                      <div style={{ width: "100%", height: "100%", backgroundColor: "#d1d5dc" }} aria-label="Image unavailable" />
                    ) : (
                      <img
                        src={image.url || image.thumbnail}
                        alt={image.alt || `Property image ${idx + 1}`}
                        onError={() => handleImageError(imgIndex)}
                      />
                    )}
                  </div>
                );
              }}
            </Item>
          );
        })}

        {/* Video thumbnail in hero strip (plain div, opens the hidden video Item at the end) */}
        {hasVideo && (
          <div
            className="commercial-hero__thumbnails-item commercial-hero__thumbnails-item--video"
            onClick={() => { if (openFunctionsRef.current[videoStartIndex]) openFunctionsRef.current[videoStartIndex](); }}
            role="button"
            tabIndex={0}
            aria-label="Play video tour"
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (openFunctionsRef.current[videoStartIndex]) openFunctionsRef.current[videoStartIndex](); } }}
          >
            {videoThumb && !errorIndexes['video-0'] ? (
              <img src={videoThumb} alt="Video thumbnail" onError={() => handleImageError('video-0')} />
            ) : (
              <div style={{ width: '100%', height: '100%', backgroundColor: '#1a2332' }} />
            )}
            <div className="commercial-hero__thumbnails-play-overlay">
              <FaPlayCircle />
            </div>
          </div>
        )}

        {/* Hidden PhotoSwipe items for remaining images (navigable in modal) */}
        {images.slice(visibleThumbnails.filter(t => t.type === 'image').length).map((img, idx) => {
          const actualIndex = visibleThumbnails.filter(t => t.type === 'image').length + idx;
          return (
            <Item
              key={`hidden-img-${actualIndex}`}
              original={normalizeUrl(img.url)}
              thumbnail={normalizeUrl(img.thumbnail || img.url)}
              width={1200}
              height={800}
            >
              {({ ref, open }) => {
                openFunctionsRef.current[actualIndex] = open;
                return <span ref={ref} style={{ display: 'none' }} onClick={open} aria-hidden="true" />;
              }}
            </Item>
          );
        })}

        {/* Video items at the END of the gallery (after all images) */}
        {videoData.map((videoUrl, idx) => {
          const actualIndex = videoStartIndex + idx;
          return (
            <Item
              key={`video-${actualIndex}`}
              html={`<div class="pswp-video-slide"><iframe src="${videoUrl}" title="Video Tour ${idx + 1}" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div>`}
            >
              {({ ref, open }) => {
                openFunctionsRef.current[actualIndex] = open;
                return <span ref={ref} style={{ display: 'none' }} onClick={open} aria-hidden="true" />;
              }}
            </Item>
          );
        })}
      </Gallery>

      {(images.length > 1 || hasVideo) && (
        <button
          className="commercial-hero__thumbnails-see-all"
          onClick={handleSeeAllClick}
          type="button"
        >
          See All
        </button>
      )}
    </div>
  );
};

export default HeroThumbnailStrip;
